/**
 * Delete a queue and any pending runs.
 */

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {toast} from '@wandb/weave/common/components/elements/Toast';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React, {useState} from 'react';

import {useEnableRunQueuePrioritizationMutation} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {Queue} from './queueUtil';

type EnableQueuePrioritizationDialogProps = {
  queue: Queue;
  onClose: () => void;
  onCommit?: () => void;
};

export const EnableQueuePrioritizationDialog = ({
  queue,
  onClose,
  onCommit,
}: EnableQueuePrioritizationDialogProps) => {
  const [isEnabling, setIsEnabling] = useState(false);
  const onClosing = () => {
    setIsEnabling(false);
    onClose();
  };

  const [enablePrioritization] = useEnableRunQueuePrioritizationMutation({
    variables: {queueID: queue.id},
    context: propagateErrorsContext(),
  });

  const onClickEnable = async () => {
    setIsEnabling(true);
    try {
      await enablePrioritization();
    } catch (e) {
      toast(
        <p>
          Error enabling prioritization on <b>{queue.name}</b>:{' '}
          {(e as any).message ?? 'Unknown error'}
        </p>,
        {type: 'error'}
      );
      return;
    }
    if (onCommit != null) {
      onCommit();
    }
    setIsEnabling(false);
    toast(
      <p>
        Prioritization enabled on <b>{queue.name}</b>
      </p>
    );
    onClose();
  };

  return (
    <Dialog onClose={onClosing} open={queue != null}>
      <DialogTitle>
        Are you sure you want to enable prioritization for this queue?
      </DialogTitle>
      <DialogContent dividers>
        With prioritization enabled, jobs will be run in priority order. Users
        will be able to specify a priority when they enqueue jobs onto{' '}
        <pre>{queue.name}</pre>
        <b>Once prioritization is enabled, it cannot be disabled.</b>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClosing} variant="secondary" size="large">
          Cancel
        </Button>
        <Button disabled={isEnabling} onClick={onClickEnable} size="large">
          Enable prioritization
        </Button>
      </DialogActions>
    </Dialog>
  );
};
